import * as React from "react";
import Modal from "../../misc/components/modal";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { WebPortalClient } from "webportalclient";
import { Form, useFormApi, useFormState, Option } from "informed";
import { Required } from "../../forms/validation/Required";
import { MultiSelect } from "../../forms/informedv2/MultiSelect";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { Dropdown } from "../../forms/informedv2/Dropdown";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";

export interface RequestAcceptanceModalProps extends RequestAcceptanceFormProps {
    show: boolean;
    error: string | undefined;
}

export interface RequestAcceptanceFormProps {
    userId: string;
    roles: any[];
    roleOptions: any[];
    decisionButtonsDisabled: boolean;
    onAccept: (userId: string, rolesIds: string[], sitesIds: string[], timeZone: string) => void;
    onClose: () => void;
}

interface RequestAcceptanceFormState {
    roles: any[];
    sites: any[];
    timeZone: string;
}

export const RequestAcceptanceForm = (props: RequestAcceptanceFormProps) => {
    const [siteOptions, setSiteOptions] = React.useState([]);
    const client = useWebPortalApiClient();
    const formApi = useFormApi<RequestAcceptanceFormState>();
    const formState = useFormState<RequestAcceptanceFormState>();
    const [timeZoneOptions, setTimeZoneOptions] = React.useState([]);

    React.useEffect(() => {
        const mapToOptions = (results: { id?: string; name?: string }[]) => results.map(v => ({ label: v.name, value: v.id }));

        const getTimeZones = async () => {
            const timeZones = await client.client.getTimeZones("id,name", undefined, "sortOrder");
            setTimeZoneOptions(mapToOptions(timeZones.value));
        }

        getTimeZones();
    }, []);

    const updateSites = async () => {
        const selectedRoles: any[] = formApi.getValue("roles");
        const rolesSelected = selectedRoles && selectedRoles.length > 0;
        if (!rolesSelected)
            return;

        const apiRolesRelatedToStudies = getApiRoles(selectedRoles).filter(r => r.studyId);
        if (apiRolesRelatedToStudies.length === 0) {
            setSiteOptions(undefined);
            formApi.setValue("sites", undefined);
            return;
        }

        const filter = rolesSelected ? `studyId in (${apiRolesRelatedToStudies.map(r => r.studyId).toString()})` : undefined;
        const select = rolesSelected ? "id,number,study" : "id,number";
        const sites = await client.client.getSites(select, filter, "number", "study($select=name)");
        const updatedOptions = sites.value.map(v => ({ label: `(${v.study.name}) ${v.number}`, value: v.id }));
        setSiteOptions(updatedOptions);
        
        const selectedSites: any[] = formApi.getValue("sites");
        if (!selectedSites)
            return;
        const updatedSites = selectedSites.filter(s => updatedOptions.map(o => o.value).includes(s.value));
        formApi.setValue("sites", updatedSites);
    }

    const getApiRoles = (selectedRoles: any[]) => props.roles.filter(r => selectedRoles.some(v => v.value === r.id));

    const isPermissionRequired = (selectedRoles: any[], permission: PermissionEnum) => {
        return getApiRoles(selectedRoles).filter(r => r.name !== "Administrator").some(r => r.permissionsIds.includes(permission));
    }

    const validateSites = (value: any, values: any) => {
        if ((!value || value.length === 0) && values.roles && values.roles.length > 0) {
            const siteRequired = isPermissionRequired(values.roles, PermissionEnum.RequiresSite);
            if (siteRequired)
                return "A site selection is required for this user's role selections";
        }
    }

    const validateForm = () => {
        formApi.validate();
        return formState.errors && Object.keys(formState.errors).length === 0 && Object.getPrototypeOf(formState.errors) === Object.prototype;
    }

    const onSubmit = () => {
        const valid = validateForm();
        if (!valid)
            return;

        const roleIds = !formState.values.roles || formState.values.roles.length === 0 ? [] : formState.values.roles.map(r => r.value);
        const siteIds = !formState.values.sites || formState.values.sites.length === 0 ? [] : formState.values.sites.map(r => r.value);
        props.onAccept(props.userId, siteIds, roleIds, formState.values.timeZone);
    }

    return (<>
        <div className="modal-body">
            <div className="form-group">
                <label>Roles</label>
                <MultiSelect
                    id="new-user-roles"
                    field="roles"
                    labelKey="label"
                    valueKey="value"
                    multi
                    onChange={updateSites}
                    options={props.roleOptions}
                    validate={Required}
                    disabled={props.decisionButtonsDisabled}
                />
            </div>
            <div className="form-group">
                <label>Sites</label>
                <MultiSelect
                    id="new-user-sites"
                    field="sites"
                    labelKey="label"
                    valueKey="value"
                    multi
                    options={siteOptions}
                    validate={validateSites}
                    disabled={props.decisionButtonsDisabled}
                />
            </div>
            <div className="form-group">
                <label htmlFor="timeZone">Preferred Time Zone</label>
                <Dropdown id="timeZone" field="timeZone" validate={Required}>
                    <Option key="OptionBlank" value=""></Option>
                    {timeZoneOptions.map(cr => (<Option key={`${cr.value}`} value={cr.value}>{cr.label}</Option>))}
                </Dropdown>
            </div>
        </div>
        <div className="modal-footer">
            <button className="btn btn-primary" onClick={onSubmit} disabled={props.decisionButtonsDisabled}>
                Accept
            </button>
            <button className="btn btn-outline btn-primary" onClick={() => props.onClose()}>
                Close
            </button>
        </div>
    </>);
}

export const RequestAcceptanceModal = (props: RequestAcceptanceModalProps) => {
    return (
        <Modal show={props.show} closeModalHandler={() => props.onClose()} header="Assign Roles to User">
            <Alert show={props.error !== undefined} category={AlertCategoryEnum.Danger}>{props.error}</Alert>
            <Form noValidate>
                <RequestAcceptanceForm
                    userId={props.userId}
                    roles={props.roles}
                    roleOptions={props.roleOptions}
                    decisionButtonsDisabled={props.decisionButtonsDisabled}
                    onAccept={props.onAccept}
                    onClose={props.onClose}
                />
            </Form>
        </Modal>
    );
}
